// ChorePop first-run onboarding.
//
// The flow is deliberately account-aware, but keeps the device-only path
// completely usable offline. Cloud sign-in starts from this screen through
// the app's existing deep-link bridge; a local Argon2id password is still
// required for every guardian.

import { validatePasswordLength } from '@chorepop/core';
import { type AppError, guardianCreate } from '@chorepop/core/client';
import { openUrl } from '@tauri-apps/plugin-opener';
import { type Component, For, Show, createEffect, createMemo, createSignal } from 'solid-js';
import {
  type CloudAuthProvider,
  type EmailTrialCredentials,
  type EmailTrialTotpEnrollment,
  type MobilePlatform,
  detectMobilePlatform,
  providersForPlatform,
} from '../cloud/cloudAuth';
import type { TrialSignInMethod } from '../cloud/trialAuthPolicy';
import { ChorePopMark } from '../components/ChorePopMark';

export type SetupCloudIdentity = {
  method: TrialSignInMethod;
  email: string;
};

export type SetupCloudStatus = 'idle' | 'opening_provider' | 'verifying' | 'error';
export type SetupSubscriptionChoice = 'subscribe_now' | 'trial';
export const FAMILY_CONTENT_POLICY_VERSION = '2026-08-29';

type Props = {
  onCreated: (
    accountId: number,
    displayName: string,
    familyName: string | undefined,
    subscriptionChoice: SetupSubscriptionChoice,
  ) => void | Promise<void>;
  platform?: MobilePlatform;
  providers?: CloudAuthProvider[];
  cloudStatus?: SetupCloudStatus;
  cloudMessage?: string | null;
  cloudIdentity?: SetupCloudIdentity | null;
  onCloudSignIn?: (provider: CloudAuthProvider) => void;
  onEmailSignIn?: (input: EmailTrialCredentials, intent: 'sign_up' | 'sign_in') => void;
  totpChallenge?: { factorId: string; enrollment?: EmailTrialTotpEnrollment } | null;
  onTotpVerify?: (factorId: string, code: string) => void;
};

export const BUCKET_PRESETS = [
  { label: '50 / 30 / 20', values: [50, 30, 20] },
  { label: '60 / 30 / 10', values: [60, 30, 10] },
  { label: '80 / 10 / 10', values: [80, 10, 10] },
] as const;

/** Number of setup steps after the welcome page. */
export function stepsIn({ tablet, relay }: { tablet: boolean; relay: boolean }): number {
  return (tablet ? 2 : 3) + (relay ? 1 : 0);
}

export const SetupScreen: Component<Props> = (props) => {
  const platform = () => props.platform ?? detectMobilePlatform();
  const availableProviders = createMemo(() => props.providers ?? providersForPlatform(platform()));
  const isTablet = () =>
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(min-width: 768px)').matches ?? window.innerWidth >= 768);

  const [step, setStep] = createSignal<
    'welcome' | 'identity' | 'email' | 'totp' | 'about' | 'family'
  >('welcome');
  const [subscriptionChoice, setSubscriptionChoice] =
    createSignal<SetupSubscriptionChoice>('trial');
  const [identity, setIdentity] = createSignal<SetupCloudIdentity | null>(null);
  const [displayName, setDisplayName] = createSignal('');
  const [familyName, setFamilyName] = createSignal('');
  const [familyCurrency, setFamilyCurrency] = createSignal<'GBP' | 'USD'>('GBP');
  const [password, setPassword] = createSignal('');
  const [presetIndex, setPresetIndex] = createSignal(0);
  const [acceptedFamilyRules, setAcceptedFamilyRules] = createSignal(false);
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);
  const [clientValidationError, setClientValidationError] = createSignal<string | null>(null);

  createEffect(() => {
    const next = props.cloudIdentity;
    if (!next) return;
    setIdentity(next);
    setErrorMessage(null);
    setStep('about');
  });

  createEffect(() => {
    if (!props.totpChallenge) return;
    clearErrors();
    setStep('totp');
  });

  const totalSteps = () => stepsIn({ tablet: isTablet(), relay: false });
  const currentStepNumber = () => {
    if (step() === 'welcome') return 1;
    if (step() === 'email' || step() === 'totp') return 2;
    if (step() === 'about') return 2;
    return isTablet() ? 2 : 3;
  };
  const showDots = () => step() !== 'welcome';

  const clearErrors = () => {
    setErrorMessage(null);
    setClientValidationError(null);
  };

  const continueFromAbout = (event: Event) => {
    event.preventDefault();
    clearErrors();
    const name = displayName().trim();
    if (!name) {
      setClientValidationError('Please enter the name your family will see.');
      return;
    }
    const passwordError = validatePasswordLength(password());
    if (passwordError) {
      setClientValidationError(passwordError.message);
      return;
    }
    setStep('family');
  };

  const createFamily = async (event: Event) => {
    event.preventDefault();
    clearErrors();
    if (!identity()) {
      setClientValidationError('Sign in with Google, Apple, or email before creating your family.');
      setStep('welcome');
      return;
    }
    if (!familyName().trim()) {
      setClientValidationError('Please give your family a name.');
      return;
    }
    if (!acceptedFamilyRules()) {
      setClientValidationError('Review and accept the Terms and Family Content Rules to continue.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await guardianCreate({
        displayName: displayName().trim(),
        password: password(),
        displayCurrencyCode: familyCurrency(),
        familyName: familyName().trim(),
        acceptedFamilyContentPolicyVersion: FAMILY_CONTENT_POLICY_VERSION,
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      setPassword('');
      await props.onCreated(
        result.value.accountId,
        result.value.displayName,
        familyName().trim(),
        subscriptionChoice(),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const back = () => {
    clearErrors();
    if (step() === 'identity') {
      setStep('welcome');
    } else if (step() === 'email' || step() === 'totp') {
      setStep('identity');
    } else if (step() === 'about') {
      setStep('welcome');
    } else if (step() === 'family') {
      setStep(isTablet() ? 'about' : 'about');
    }
  };

  return (
    <main class="setup-flow" data-tablet={isTablet() ? 'true' : 'false'}>
      <Show when={showDots()}>
        <div
          class="setup-step-meta"
          aria-label={`Setup step ${currentStepNumber()} of ${totalSteps()}`}
        >
          <button type="button" class="setup-back-button" onClick={back} disabled={submitting()}>
            ← Back
          </button>
          <span>
            Step {currentStepNumber()} of {totalSteps()}
          </span>
          <span class="setup-step-dots" aria-hidden="true">
            <For each={Array.from({ length: totalSteps() })}>
              {(_, index) => <i classList={{ active: index() + 1 <= currentStepNumber() }} />}
            </For>
          </span>
        </div>
      </Show>

      <Show when={step() === 'welcome'}>
        <section class="setup-welcome">
          <div class="setup-welcome-brand">
            <ChorePopMark class="setup-brand-mark" title="ChorePop" />
            <p class="setup-eyebrow">Welcome to</p>
            <h1>
              Chore<span>Pop</span>
            </h1>
            <p class="setup-value-statement">
              Chores, pocket money and a ledger that matches the cash tin on the shelf.
            </p>
          </div>

          <div class="setup-actions" data-testid="setup-subscription-choice">
            <button
              type="button"
              class="primary setup-subscription-button"
              onClick={() => {
                setSubscriptionChoice('subscribe_now');
                setStep('identity');
              }}
            >
              Sign Up Now — £2.99/month
            </button>
            <button
              type="button"
              class="secondary setup-subscription-button"
              onClick={() => {
                setSubscriptionChoice('trial');
                setStep('identity');
              }}
            >
              Try 30 Days Free
            </button>
            <p class="setup-footnote">One family subscription. Cancel through your app store.</p>
          </div>

          <div class="setup-trust-row" aria-label="ChorePop benefits">
            <span>Works offline</span>
            <span>No ads displayed</span>
            <span>Family-only data</span>
          </div>
        </section>
      </Show>

      <Show when={step() === 'identity'}>
        <section class="setup-welcome setup-identity-choice">
          <div class="setup-welcome-brand">
            <ChorePopMark class="setup-brand-mark" title="ChorePop" />
            <p class="setup-eyebrow">
              {subscriptionChoice() === 'subscribe_now' ? 'Subscribe now' : '30-day free trial'}
            </p>
            <h1>Choose your guardian sign-in</h1>
            <p class="setup-value-statement">
              Use Apple, Google or email. Kids will get their own ChorePop PIN later.
            </p>
          </div>

          <div class="setup-actions">
            <Show when={props.cloudMessage || errorMessage()}>
              <div class="form-error setup-inline-error" role="alert">
                {props.cloudMessage ?? errorMessage()}
              </div>
            </Show>
            <Show when={availableProviders().includes('apple')}>
              <ProviderButton
                provider="apple"
                disabled={
                  submitting() ||
                  props.cloudStatus === 'opening_provider' ||
                  props.cloudStatus === 'verifying'
                }
                onClick={() => props.onCloudSignIn?.('apple')}
              />
            </Show>
            <Show when={availableProviders().includes('google')}>
              <ProviderButton
                provider="google"
                disabled={
                  submitting() ||
                  props.cloudStatus === 'opening_provider' ||
                  props.cloudStatus === 'verifying'
                }
                onClick={() => props.onCloudSignIn?.('google')}
              />
            </Show>
            <Show when={availableProviders().length > 0}>
              <div class="setup-divider">
                <span>or</span>
              </div>
            </Show>
            <button type="button" class="setup-device-button" onClick={() => setStep('email')}>
              Continue with email
            </button>
            <p class="setup-footnote">Your family setup stays private to your ChorePop account.</p>
          </div>
        </section>
      </Show>

      <Show when={step() === 'email'}>
        <EmailTrialPanel
          busy={props.cloudStatus === 'opening_provider' || props.cloudStatus === 'verifying'}
          message={props.cloudMessage ?? errorMessage()}
          onSubmit={(input, intent) => props.onEmailSignIn?.(input, intent)}
        />
      </Show>

      <Show when={step() === 'totp' && props.totpChallenge} keyed>
        {(challenge) => (
          <TotpTrialPanel
            challenge={challenge}
            busy={props.cloudStatus === 'opening_provider' || props.cloudStatus === 'verifying'}
            message={props.cloudMessage ?? errorMessage()}
            onVerify={(code) => props.onTotpVerify?.(challenge.factorId, code)}
          />
        )}
      </Show>

      <Show when={step() === 'about' || (step() === 'family' && isTablet())}>
        <section class="setup-form-layout setup-about-family">
          <form class="setup-panel setup-about-panel" onSubmit={continueFromAbout} novalidate>
            <Show when={identity()}>
              <div class="setup-identity-card">
                <IdentityGlyph method={identity()?.method ?? 'google'} />
                <div>
                  <strong>
                    Signed in with{' '}
                    {identity()?.method === 'apple'
                      ? 'Apple'
                      : identity()?.method === 'google'
                        ? 'Google'
                        : 'email'}
                  </strong>
                  <span>{identity()?.email}</span>
                </div>
                <span class="setup-verified" aria-label="Verified">
                  ✓
                </span>
              </div>
            </Show>
            <p class="setup-eyebrow">About you</p>
            <h1>What should the kids call you?</h1>
            <p class="setup-copy">
              Shown on approvals and notes. Apple doesn&apos;t share your name, so we ask.
            </p>
            <label class="field" for="setup-display-name">
              <span class="field-label">Your name</span>
              <input
                id="setup-display-name"
                type="text"
                autocomplete="name"
                required
                value={displayName()}
                disabled={submitting()}
                onInput={(e) => {
                  setDisplayName(e.currentTarget.value);
                  clearErrors();
                }}
              />
            </label>
            <label class="field" for="setup-device-password">
              <span class="field-label">Device password</span>
              <input
                id="setup-device-password"
                type="password"
                autocomplete="new-password"
                required
                value={password()}
                disabled={submitting()}
                onInput={(e) => {
                  setPassword(e.currentTarget.value);
                  clearErrors();
                }}
              />
              <span class="field-hint">
                For signing in when there&apos;s no internet. Minimum 10 characters.
              </span>
            </label>
            <div class="setup-info-card">
              Two ways in: your account for syncing, this password for offline. You&apos;ll want
              both.
            </div>
            <Show when={clientValidationError() && !isTablet()}>
              <div class="form-error" role="alert">
                {clientValidationError()}
              </div>
            </Show>
            <Show when={!isTablet()}>
              <button type="submit" class="primary" disabled={submitting()}>
                Continue
              </button>
            </Show>
          </form>
          <Show when={isTablet() && step() === 'about'}>
            <FamilyPanel
              familyName={familyName()}
              setFamilyName={setFamilyName}
              familyCurrency={familyCurrency()}
              setFamilyCurrency={setFamilyCurrency}
              presetIndex={presetIndex()}
              setPresetIndex={setPresetIndex}
              acceptedFamilyRules={acceptedFamilyRules()}
              setAcceptedFamilyRules={setAcceptedFamilyRules}
              submitting={submitting()}
              onSubmit={createFamily}
              validationError={clientValidationError()}
              errorMessage={errorMessage()}
            />
          </Show>
        </section>
      </Show>

      <Show when={step() === 'family' && !isTablet()}>
        <section class="setup-single-panel">
          <FamilyPanel
            familyName={familyName()}
            setFamilyName={setFamilyName}
            familyCurrency={familyCurrency()}
            setFamilyCurrency={setFamilyCurrency}
            presetIndex={presetIndex()}
            setPresetIndex={setPresetIndex}
            acceptedFamilyRules={acceptedFamilyRules()}
            setAcceptedFamilyRules={setAcceptedFamilyRules}
            submitting={submitting()}
            onSubmit={createFamily}
            validationError={clientValidationError()}
            errorMessage={errorMessage()}
          />
        </section>
      </Show>
    </main>
  );
};

const ProviderButton: Component<{
  provider: CloudAuthProvider;
  disabled?: boolean;
  onClick: () => void;
}> = (props) => (
  <button
    type="button"
    class={`setup-provider-button setup-provider-button--${props.provider}`}
    disabled={props.disabled}
    onClick={props.onClick}
  >
    <ProviderGlyph provider={props.provider} />
    <span>Continue with {props.provider === 'apple' ? 'Apple' : 'Google'}</span>
  </button>
);

const ProviderGlyph: Component<{ provider: CloudAuthProvider }> = (props) =>
  props.provider === 'apple' ? (
    <span class="provider-glyph provider-glyph--apple" aria-hidden="true">
      
    </span>
  ) : (
    <span class="provider-glyph provider-glyph--google" aria-hidden="true">
      G
    </span>
  );

const IdentityGlyph: Component<{ method: TrialSignInMethod }> = (props) =>
  props.method === 'email_password' ? (
    <span class="provider-glyph provider-glyph--email" aria-hidden="true">
      ✉
    </span>
  ) : (
    <ProviderGlyph provider={props.method} />
  );

const EmailTrialPanel: Component<{
  busy: boolean;
  message?: string | null;
  onSubmit: (input: EmailTrialCredentials, intent: 'sign_up' | 'sign_in') => void;
}> = (props) => {
  const [email, setEmail] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [intent, setIntent] = createSignal<'sign_up' | 'sign_in'>('sign_up');
  const [error, setError] = createSignal<string | null>(null);

  const submit = (event: Event) => {
    event.preventDefault();
    const value = email().trim();
    if (!/^\S+@\S+\.\S+$/.test(value)) {
      setError('Enter a valid email address.');
      return;
    }
    if (password().length < 10) {
      setError('Use at least 10 characters for your email sign-in password.');
      return;
    }
    setError(null);
    props.onSubmit({ email: value, password: password() }, intent());
  };

  return (
    <section class="setup-verify-panel">
      <p class="setup-eyebrow">Guardian sign-in</p>
      <h1>{intent() === 'sign_up' ? 'Create your sign-in' : 'Welcome back'}</h1>
      <p class="setup-copy">
        Email sign-in uses a verified email and an authenticator app. Your family&apos;s trial
        ledger stays only on this device.
      </p>
      <form onSubmit={submit} novalidate>
        <label class="field" for="setup-email-address">
          <span class="field-label">Email address</span>
          <input
            id="setup-email-address"
            type="email"
            autocomplete="email"
            value={email()}
            disabled={props.busy}
            onInput={(event) => {
              setEmail(event.currentTarget.value);
              setError(null);
            }}
          />
        </label>
        <label class="field" for="setup-email-password">
          <span class="field-label">Email sign-in password</span>
          <input
            id="setup-email-password"
            type="password"
            autocomplete={intent() === 'sign_up' ? 'new-password' : 'current-password'}
            value={password()}
            disabled={props.busy}
            onInput={(event) => {
              setPassword(event.currentTarget.value);
              setError(null);
            }}
          />
        </label>
        <Show when={error() || props.message}>
          <div class="form-error" role="alert">
            {error() ?? props.message}
          </div>
        </Show>
        <button type="submit" class="primary" disabled={props.busy}>
          {intent() === 'sign_up' ? 'Create email sign-in' : 'Sign in with email'}
        </button>
      </form>
      <button
        type="button"
        class="setup-join-button"
        disabled={props.busy}
        onClick={() => {
          setError(null);
          setIntent((current) => (current === 'sign_up' ? 'sign_in' : 'sign_up'));
        }}
      >
        {intent() === 'sign_up' ? 'I already have an account' : 'Create a new account'}
      </button>
    </section>
  );
};

const TotpTrialPanel: Component<{
  challenge: { factorId: string; enrollment?: EmailTrialTotpEnrollment };
  busy: boolean;
  message?: string | null;
  onVerify: (code: string) => void;
}> = (props) => {
  const [code, setCode] = createSignal('');
  const [error, setError] = createSignal<string | null>(null);

  const submit = (event: Event) => {
    event.preventDefault();
    const value = code().replace(/\s/g, '');
    if (!/^\d{6}$/.test(value)) {
      setError('Enter the six-digit code from your authenticator app.');
      return;
    }
    setError(null);
    props.onVerify(value);
  };

  return (
    <section class="setup-verify-panel" data-testid="setup-totp-panel">
      <p class="setup-eyebrow">Guardian security</p>
      <h1>Secure your sign-in</h1>
      <p class="setup-copy">
        Use an authenticator app to scan this code, then enter its six-digit ChorePop code.
      </p>
      <Show when={props.challenge.enrollment}>
        <div class="setup-totp-card">
          <img
            src={props.challenge.enrollment?.qrCode}
            alt="Authenticator setup QR code"
            class="setup-totp-qr"
          />
          <span class="field-hint">
            Can&apos;t scan it? Enter this setup key in your authenticator app:
          </span>
          <code class="setup-totp-secret">{props.challenge.enrollment?.secret}</code>
        </div>
      </Show>
      <form onSubmit={submit} novalidate>
        <label class="field" for="setup-totp-code">
          <span class="field-label">Authenticator code</span>
          <input
            id="setup-totp-code"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            value={code()}
            disabled={props.busy}
            onInput={(event) => {
              setCode(event.currentTarget.value.replace(/\D/g, '').slice(0, 6));
              setError(null);
            }}
          />
        </label>
        <Show when={error() || props.message}>
          <div class="form-error" role="alert">
            {error() ?? props.message}
          </div>
        </Show>
        <button type="submit" class="primary" disabled={props.busy}>
          Verify authenticator
        </button>
      </form>
    </section>
  );
};

type FamilyPanelProps = {
  familyName: string;
  setFamilyName: (value: string) => void;
  familyCurrency: 'GBP' | 'USD';
  setFamilyCurrency: (value: 'GBP' | 'USD') => void;
  presetIndex: number;
  setPresetIndex: (value: number) => void;
  acceptedFamilyRules: boolean;
  setAcceptedFamilyRules: (value: boolean) => void;
  submitting: boolean;
  onSubmit: (event: Event) => void;
  validationError: string | null;
  errorMessage: string | null;
};

const FamilyPanel: Component<FamilyPanelProps> = (props) => {
  const preset = () => BUCKET_PRESETS[props.presetIndex] ?? BUCKET_PRESETS[0];
  return (
    <form class="setup-panel setup-family-panel" onSubmit={props.onSubmit} novalidate>
      <p class="setup-eyebrow">Your family</p>
      <h1>Make it yours</h1>
      <p class="setup-copy">
        A friendly name for the home screen and a starting plan for every coin.
      </p>
      <label class="field" for="setup-family-name">
        <span class="field-label">Family name</span>
        <input
          id="setup-family-name"
          type="text"
          required
          value={props.familyName}
          disabled={props.submitting}
          onInput={(e) => props.setFamilyName(e.currentTarget.value)}
        />
        <span class="field-hint">Just for the greeting on the home screen.</span>
      </label>
      <div class="field">
        <label class="field-label" for="setup-family-currency">
          Family currency
        </label>
        <select
          id="setup-family-currency"
          value={props.familyCurrency}
          disabled={props.submitting}
          aria-describedby="setup-family-currency-hint"
          onChange={(event) =>
            props.setFamilyCurrency(event.currentTarget.value === 'USD' ? 'USD' : 'GBP')
          }
        >
          <option value="GBP">Pounds sterling (£)</option>
          <option value="USD">US dollars ($)</option>
        </select>
        <span id="setup-family-currency-hint" class="field-hint">
          Everyone in the family uses this currency. Overseas gifts and holiday spending can still
          record a different currency.
        </span>
      </div>
      <fieldset class="setup-split-fieldset">
        <legend>Starting bucket split</legend>
        <div class="setup-split-bar" aria-label={`Starting split ${preset().label}`}>
          <span style={{ width: `${preset().values[0]}%`, 'background-color': 'var(--spend)' }} />
          <span style={{ width: `${preset().values[1]}%`, 'background-color': 'var(--save)' }} />
          <span style={{ width: `${preset().values[2]}%`, 'background-color': 'var(--tithe)' }} />
        </div>
        <div class="setup-split-labels">
          <span>Spend</span>
          <span>Save</span>
          <span>Give</span>
        </div>
        <div class="setup-preset-grid">
          <For each={BUCKET_PRESETS}>
            {(choice, index) => (
              <button
                type="button"
                classList={{ active: props.presetIndex === index() }}
                onClick={() => props.setPresetIndex(index())}
                disabled={props.submitting}
              >
                {choice.label}
              </button>
            )}
          </For>
        </div>
        <span class="field-hint">You can change this per child later.</span>
      </fieldset>
      <label class="setup-policy-acceptance" for="setup-family-rules-acceptance">
        <input
          id="setup-family-rules-acceptance"
          type="checkbox"
          checked={props.acceptedFamilyRules}
          disabled={props.submitting}
          onChange={(event) => props.setAcceptedFamilyRules(event.currentTarget.checked)}
        />
        <span>
          I accept ChorePop&apos;s{' '}
          <button
            type="button"
            class="link-button"
            onClick={() => void openUrl('https://chorepop.com/terms')}
          >
            Terms of use and Family Content Rules
          </button>
          . I understand family members can report unsafe text privately to ChorePop.
        </span>
      </label>
      <Show when={props.validationError}>
        <div class="form-error" role="alert">
          {props.validationError}
        </div>
      </Show>
      <Show when={props.errorMessage}>
        <div class="form-error" role="alert">
          {props.errorMessage}
        </div>
      </Show>
      <button type="submit" class="primary" disabled={props.submitting}>
        {props.submitting ? 'Creating your family…' : 'Create my family'}
      </button>
    </form>
  );
};

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
    case 'conflict':
    case 'network_required':
      return error.message;
    case 'unauthorized':
      return "You don't have permission to do that.";
    case 'not_found':
      return 'Something we needed was missing. Please try again.';
    default:
      return `Something went wrong: ${error.message}`;
  }
}
